import { useEffect, useMemo, useState } from 'react';

import WidgetGroup from '@components/WidgetGroup';
import AppGrid from '@layout/AppGrid';
import PageHeader from '@layout/PageHeader';
import Coach from '@widgets/Coach';
import CreditWidget from '@widgets/CreditWidget';
import LatestMessages from '@widgets/LatestMessages';
import LeagueStandings from '@widgets/LeagueStandings';
import Merch from '@widgets/Merch';
import PlayerDiscipline from '@widgets/PlayerDiscipline';
import PlayerFullInfo from '@widgets/PlayerFullInfo';
import TeamStats from '@widgets/TeamStats';
import Ticket from '@widgets/Ticket';

import AuthService from 'network/AuthService';
import ClientController from 'network/ClientController';

const unwrap = (value) => value?.data ?? value;

const getPlayers = (teamSection) => {
  const data = unwrap(teamSection);
  const players = data?.players ?? data?.team?.players ?? data?.club?.players;
  return Array.isArray(players) ? players : [];
};

const isCaptain = (player) =>
  player?.isCaptain === true ||
  player?.is_captain === true ||
  player?.captain === true ||
  player?.role?.toLowerCase?.() === 'captain';

const Teamdash = () => {
  const authService = useMemo(() => new AuthService(), []);
  const clientController = useMemo(() => new ClientController(), []);
  const teamID = authService.getTeamID();

  const [team, setTeam] = useState(null);
  const [teamSection, setTeamSection] = useState(null);
  const [nextMatch, setNextMatch] = useState(null);
  const [league, setLeague] = useState(null);
  const [leagueCode, setLeagueCode] = useState(null);
  const [table, setTable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!teamID) {
      setError('No team is associated with the current account.');
      return undefined;
    }

    let alive = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError(null);

      try {
        const [clubResult, sectionResult, matchResult] = await Promise.allSettled([
          clientController.fetchClub(teamID),
          clientController.fetchTeamSectionClub(teamID),
          clientController.fetchNextMatch(teamID),
        ]);

        if (!alive) return;

        const clubDetail = clubResult.status === 'fulfilled' ? unwrap(clubResult.value) : null;
        const sectionDetail = sectionResult.status === 'fulfilled' ? unwrap(sectionResult.value) : null;
        const sectionTeam = sectionDetail?.team ?? sectionDetail?.club ?? null;
        const mergedTeam = { ...(clubDetail || {}), ...(sectionTeam || {}) };

        setTeam(Object.keys(mergedTeam).length ? mergedTeam : null);
        setTeamSection(sectionDetail);
        setNextMatch(matchResult.status === 'fulfilled' ? unwrap(matchResult.value) : null);

        const embeddedLeague = mergedTeam?.league ?? sectionDetail?.league ?? null;
        const leagueId =
          embeddedLeague?.id ?? mergedTeam?.league_id ?? mergedTeam?.leagueId ?? null;
        const fallbackCode =
          embeddedLeague?.code ??
          embeddedLeague?.league_code ??
          mergedTeam?.league_code ??
          mergedTeam?.leagueCode ??
          authService.getPrimaryTeamParsed()?.['League Code'] ??
          null;

        let leagueDetail = embeddedLeague;
        try {
          if (leagueId) {
            leagueDetail = unwrap(await clientController.fetchLeagueById(leagueId));
          } else if (fallbackCode) {
            leagueDetail = unwrap(await clientController.fetchLeague(fallbackCode));
          }
        } catch (leagueError) {
          if (!leagueDetail && !fallbackCode) throw leagueError;
        }

        if (!alive) return;

        const code = leagueDetail?.code ?? leagueDetail?.league_code ?? fallbackCode;
        setLeague(leagueDetail);
        setLeagueCode(code || null);

        if (code) {
          const tableResponse = unwrap(await clientController.fetchTable(code));
          if (!alive) return;
          const rows = Array.isArray(tableResponse)
            ? tableResponse
            : Array.isArray(tableResponse?.table)
              ? tableResponse.table
              : [];
          setTable(rows);
        } else {
          setTable([]);
        }

        const initialFailures = [clubResult, sectionResult, matchResult].filter(
          (result) => result.status === 'rejected',
        );
        if (initialFailures.length === 3) {
          throw initialFailures[0].reason;
        }
      } catch (loadError) {
        if (alive) {
          setError(loadError?.message || 'Failed to load dashboard data.');
        }
      } finally {
        if (alive) setLoading(false);
      }
    };

    loadDashboard();
    return () => { alive = false; };
  }, [authService, clientController, teamID]);

  const players = getPlayers(teamSection);
  const captain =
    players.find(isCaptain) ??
    (typeof team?.captain === 'object' ? team.captain : null) ??
    (team?.captain ? { name: team.captain } : null);
  const disciplineLeader = players.reduce((leader, player) => {
    const cards = Number(player?.yellow_card ?? player?.yellowCard ?? 0) +
      Number(player?.red_card ?? player?.redCard ?? 0) * 2;
    const leaderCards = Number(leader?.yellow_card ?? leader?.yellowCard ?? 0) +
      Number(leader?.red_card ?? leader?.redCard ?? 0) * 2;
    return cards > leaderCards ? player : leader;
  }, null);
  const currentTableRow = table.find((row) =>
    String(row?.id ?? row?.team_id ?? row?.teamId) === String(teamID),
  );
  const stats =
    teamSection?.stats ??
    teamSection?.statistics ??
    team?.stats ??
    team?.statistics ??
    currentTableRow ??
    null;

  const widgets = {
    nextMatch: <Ticket match={nextMatch} loading={loading} />,
    table: (
      <LeagueStandings
        table={table}
        leagueName={league?.name ?? league?.title ?? 'League table'}
        loading={loading}
      />
    ),
    stats: <TeamStats team={team} stats={stats} />,
    credit: <CreditWidget teamID={teamID} />,
    group: (
      <WidgetGroup>
        <Coach coach={team?.coach ?? teamSection?.coach} team={team} />
        <PlayerDiscipline player={disciplineLeader} team={team} />
      </WidgetGroup>
    ),
    inbox: <Merch />,
    captain: <PlayerFullInfo player={captain} />,
    messages: <LatestMessages />,
  };

  const teamName = team?.team_name ?? team?.name ?? teamSection?.name;
  const title = teamName ? `${teamName} Dashboard` : 'Team Dashboard';

  return (
    <>
      <PageHeader title={title} />
      {leagueCode && <span className="label h6">League: {leagueCode}</span>}
      {error && (
        <div className="card card-padded" role="alert" style={{ marginBottom: 20 }}>
          {error}
        </div>
      )}
      <AppGrid id="team_dashboard" widgets={widgets} />
    </>
  );
};

export default Teamdash;
